import type { ReportColumn } from '~/gql/reports/reports'
import { ArrowDown, ArrowUp } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '~/components/ui/button'
import { tourAnchor } from '~/helpers/tour/anchors'
import CheckboxGroup from './CheckboxGroup'

const LEVELS = ['learner', 'enrollment', 'module'] as const

type Props = {
  columns: ReportColumn[]
  selected: string[]
  onToggle: (key: string) => void
  onMove: (key: string, delta: -1 | 1) => void
}

/**
 * Les colonnes du rapport (#163) : à cocher par niveau (apprenant, parcours,
 * module), puis rangées dans l'ordre du fichier. Une case cochée part en fin
 * de fichier ; les flèches la déplacent.
 */
function ReportColumnPicker({ columns, selected, onToggle, onMove }: Props) {
  const { t, i18n } = useTranslation()
  const lang = i18n.language.startsWith('en') ? 'en' : 'fr'
  const labels = new Map(columns.map(column => [column.key, column.label[lang]]))
  const order = selected.filter(key => labels.has(key))

  return (
    <div {...tourAnchor('reports.columns')} className="grid gap-6 md:grid-cols-2">
      <div className="flex flex-col gap-4">
        {LEVELS.map((level) => {
          const options = columns.filter(column => column.level === level).map(column => ({ id: column.key, label: column.label[lang] }))
          return options.length > 0 && (
            <CheckboxGroup key={level} title={t(`reports.level.${level}`)} options={options} selected={selected} onToggle={onToggle} />
          )
        })}
      </div>
      <div className="flex flex-col gap-2">
        <p className="text-sm font-medium">{t('reports.columns.order')}</p>
        {order.length === 0
          ? <p role="alert" className="text-sm text-destructive">{t('reports.columns.required')}</p>
          : (
              <ol className="flex flex-col gap-1">
                {order.map((key, index) => (
                  <li key={key} className="flex items-center gap-2 rounded-md border px-2 py-1 text-sm">
                    <span className="w-6 text-right text-muted-foreground">{index + 1}</span>
                    <span className="flex-1">{labels.get(key)}</span>
                    <Button type="button" variant="ghost" size="icon" disabled={index === 0} aria-label={t('reports.columns.up', { column: labels.get(key) })} onClick={() => onMove(key, -1)}>
                      <ArrowUp className="size-4" />
                    </Button>
                    <Button type="button" variant="ghost" size="icon" disabled={index === order.length - 1} aria-label={t('reports.columns.down', { column: labels.get(key) })} onClick={() => onMove(key, 1)}>
                      <ArrowDown className="size-4" />
                    </Button>
                  </li>
                ))}
              </ol>
            )}
      </div>
    </div>
  )
}

export default ReportColumnPicker
